import React, { useState, useEffect } from 'react';
import { Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { FileText, InfoCircle, Search } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { Share } from '@capacitor/share';
import { Browser } from '@capacitor/browser';
import { useTranslation } from 'react-i18next';
import useCollections, { CollectionModal, CollectionIcon } from '../../hooks/useCollections';

const openCapacitorSite = async (link) => {
  await Browser.open({ url: link });
};

function formatDate(inputDate) {
  if (!inputDate) return '';
  
  // If it's already in M-D-YYYY format, return as-is
  if (typeof inputDate === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(inputDate)) {
    return inputDate;
  }
  
  try {
    const date = new Date(inputDate);
    if (isNaN(date.getTime())) return '';
    
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  } catch (error) {
    return '';
  }
}

const SponsoredBills = ({ bioGuideID }) => {
  const { t } = useTranslation('common');
  var [feedLength, setFeedLength] = useState(50);
  const [billInfo, setBillInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const apiKey = process.env.REACT_APP_API_KEY;
  const navigate = useNavigate();

  // Use the collection hook
  const {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    openCollectionModal,
    closeModal,
    handleSave,
    isStorageAvailable
  } = useCollections();

  // Moved inside component (was module-scope) so it can use t() for the share-sheet strings
  const handleNativeShare = async (id) => {
    const baseUrl = 'https://news.govglance.org';
    const fullUrl = `${baseUrl}/feed/united_states_of_america/congressional_bills/${id}`;

    try {
      const result = await Share.share({
        title: t('common:shareCheckThisOut', 'Check this out!'),
        url: fullUrl,
        dialogTitle: t('common:shareWithDialog', 'Share with'),
      });

      if (result.action === Share.sharedAction) {
        // console.log('Shared successfully');
      } else if (result.action === Share.dismissedAction) {
        // console.log('Share canceled');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  // Built separately from the click handler so the folder icon can check the
  // same object against the saved collection.
  // feedName/branch/topic stay English per stored-data convention
  const buildCollectionItem = (bill) => {
    if (!bill || !bill.details) return null;

    return {
      title: bill.details.title,
      url: bill.details.url || '',
      docLink: bill.details.download?.pdfLink || '',
      date: bill.details.created_at || bill.details.date_issued, // Raw date
      feedName: 'Bill Updates',
      branch: 'Legislative',
      topic: 'Sponsored Bills'
    };
  };

  // Handle adding to collection
  const handleAddToCollection = (bill) => {
    const collectionItem = buildCollectionItem(bill);
    if (!collectionItem) return;
    openCollectionModal(collectionItem);
  };

  const fetchBillInfo = () => {
    setLoading(true);

    fetch(`https://api.govglance.org/members/{bio_guid_id}?bio_guide_id=${bioGuideID}&limit=${feedLength}&skip=0&search=${searchQuery}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        "Authorization": "Bearer " + apiKey,
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch bill info');
        }
        return response.json();
      })
      .then(data => {
        const billDetailsPromises = [];

        data.forEach(bill => {
          const billDetailPromise = fetch(`https://api.govglance.org/members/congresssional_bill/${bill.id}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              "Authorization": "Bearer " + apiKey,
            }
          }).then(response => {
            if (!response.ok) {
              throw new Error('Failed to fetch bill details');
            }
            return response.json();
          });

          billDetailsPromises.push(billDetailPromise);
        });

        return Promise.all(billDetailsPromises)
          .then(billDetails => {
            const combinedData = data.map((bill, index) => ({
              ...bill,
              details: billDetails[index]
            }));

            setBillInfo(combinedData);
            setLoading(false);
            setError(null);
          });
      })
      .catch(error => {
        console.error('Error fetching bill info:', error);
        setError('Error fetching bill info');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchBillInfo();
  }, [searchQuery]);

  const handleSearchInputChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleDetailsClick = (id) => {
    navigate(`/feed/united_states_of_america/congressional_bills/${id}`);
  };

  const renderData = () => {
    if (loading) {
      return <Spinner id='spinner' animation="grow" variant="light" />;
    }

    if (error) {
      return <p>{t('common:errorFetchingData', 'Error fetching data.')}</p>;
    }

    if (!billInfo || billInfo.length === 0) {
      return <p>{t('common:noDataAvailable', 'No data available.')}</p>;
    }

    const filteredBillInfo = billInfo.filter(bill => {
      return bill.details.title.toLowerCase().includes(searchQuery.toLowerCase());
    });

    return filteredBillInfo.map((bill, index) => (
      <div id='fullBillFeed' key={index}>
        <Container id='feedContainerNormalView'>
          <Card.Title onClick={(e) => {
            e.preventDefault();
            openCapacitorSite(bill.details.download.pdfLink);
          }} id='billText'>
            {bill.role} - {bill.details.title.length > 180 ? `${bill.details.title.slice(0, 177)}...` : bill.details.title}
          </Card.Title>

          <Card.Body id='billBodyText'>
            {formatDate(bill.details.created_at)}
          </Card.Body>

          <Button id='billLink' onClick={() => handleDetailsClick(bill.details.id)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> {t('common:more', 'More')}
          </Button>
          
          <Button id='viewBillButton' onClick={(e) => {
            e.preventDefault();
            openCapacitorSite(bill.details.download.pdfLink);
          }}>
            <FileText /> {t('common:bill', 'Bill')}
          </Button>

          <Button id='addToCollectionButton' onClick={() => handleAddToCollection(bill)}>
            <CollectionIcon item={buildCollectionItem(bill)} />
          </Button>

          <Button id="shareButton" onClick={() => handleNativeShare(bill.details.id)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-send-horizontal-icon lucide-send-horizontal"><path d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z"/><path d="M6 12h16"/></svg>
          </Button>
        </Container>
      </div>
    ));
  };

  return (
    <>
      <div id='searchForm'>
        <Form className="text-center">
          <Form.Group className="d-flex justify-content-center align-items-center">
            <Form.Control
              variant="light"
              id="searchInput"
              placeholder={t('common:searchPlaceholder', 'Search...')}
              type="text"
              color="white"
              value={searchQuery}
              onChange={handleSearchInputChange}
            />
            <Button id='ggButton'>
              <Search />
            </Button>
          </Form.Group>
        </Form>
      </div>
      <div id='fullBillFeed'>
        {renderData()}
      </div>

      {/* Collection Modal */}
      <CollectionModal
        isModalOpen={isModalOpen}
        currentItem={currentItem}
        saveSuccess={saveSuccess}
        note={note}
        setNote={setNote}
        handleSave={handleSave}
        closeModal={closeModal}
        isStorageAvailable={isStorageAvailable}
      />
    </>
  );
}

export default SponsoredBills;